import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Role, Permission } from '../../../../interfaces/user.interface';
import { EntityService } from '../../../../services/entity.service';

@Component({
  selector: 'app-edit-role-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  template: `
    <div class="modal-header">
      <h4 class="modal-title">{{role ? 'Modifier' : 'Ajouter'}} un rôle</h4>
      <button type="button" class="btn-close" (click)="activeModal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <form [formGroup]="roleForm">
        <div class="mb-3">
          <label class="form-label">Nom</label>
          <input type="text" class="form-control" formControlName="name">
        </div>
        <div class="mb-3">
          <label class="form-label">Nom affiché</label>
          <input type="text" class="form-control" formControlName="display_name">
        </div>
        <div class="mb-3">
          <div class="row">
            <div class="col-md-6">
              <label class="form-label">Permissions</label>
              <div class="d-flex" *ngIf="loadingPermissions">
                <div class="loader-container">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Chargement...</span>
                  </div>
                  
                </div>
                <span class="ms-2" style="margin-top: 15px;">Chargement des permissions...</span>
              </div>
              
            </div>
            <div class="col-md-6">
              <div class="mb-3" style="max-width: 300px;">
                <input type="text" 
                       class="form-control search-permissions" 
                       placeholder="Rechercher des permissions..." 
                       [(ngModel)]="searchQuery" 
                       [ngModelOptions]="{standalone: true}"
                     (ngModelChange)="filterPermissions()">
            </div>
            </div>
          </div>
          <ng-container *ngIf="!loadingPermissions">
            <div class="mb-2">
              <div class="form-check" style="margin-left: 20px;">
                <input type="checkbox" class="form-check-input" id="selectAll"
                  [checked]="isAllSelected()"
                  (change)="toggleAll()">
                <label class="form-check-label" for="selectAll">
                  Tout sélectionner
                </label>
              </div>
            </div>

            <div class="permissions-grid">
              <div *ngFor="let perm of filteredPermissions" class="permission-item">
                <input type="checkbox" 
                  [id]="'perm_' + perm.id"
                  [checked]="isPermissionSelected(perm.id)"
                  (change)="togglePermission(perm.id)">
                <label [for]="'perm_' + perm.id">{{perm.description}}</label>
              </div>
            </div>
          </ng-container>
        </div>
      </form>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">Annuler</button>
      <button type="button" class="btn btn-primary" (click)="save()" [disabled]="!roleForm.valid">
        {{role ? 'Mettre à jour' : 'Ajouter'}}
      </button>
    </div>
  `,
  styles: [`
    .permissions-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 10px;
      margin-top: 10px;
      overflow-y: scroll;
    height: 500px;
    }
    .permission-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px;
    }
    .form-check {
      margin-bottom: 10px;
      padding-left: 0;
    }
    .loader-container {
      display: flex;
      align-items: center;
      padding: 1rem 0;
    }
    .search-permissions {
      width: 200px;
      height: 35px;
      margin-left: auto;
      font-size: 12px;
    }
  `]
})
export class EditRoleModalComponent implements OnInit {
  @Input() role?: Role;
  roleForm: FormGroup;
  allPermissions: Permission[] = [];
  filteredPermissions: Permission[] = [];
  selectedPermissions: Set<number> = new Set();
  loadingPermissions = false;
  searchQuery: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private entityService: EntityService,
    private fb: FormBuilder
  ) {
    this.roleForm = this.fb.group({
      name: ['', Validators.required],
      display_name: ['', Validators.required]
    });
  }

  ngOnInit() {
    if (this.role) {
      this.roleForm.patchValue({
        name: this.role.name,
        display_name: this.role.display_name
      });
      this.selectedPermissions = new Set(this.role.permissions.map(p => p.id));
    }
    this.loadPermissions();
  }

  loadPermissions() {
    this.loadingPermissions = true;
    this.entityService.getDatas('/admin/permissions').subscribe({
      next: (response: any) => {
        this.allPermissions = response;
        this.filterPermissions(); // Appliquer le filtre initial
        this.loadingPermissions = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des permissions:', error);
        this.loadingPermissions = false;
      }
    });
  }

  filterPermissions() {
    if (!this.searchQuery.trim()) {
      this.filteredPermissions = [...this.allPermissions];
      return;
    }

    const searchLower = this.searchQuery.toLowerCase().trim();
    this.filteredPermissions = this.allPermissions.filter(perm =>
      perm.description.toLowerCase().includes(searchLower)
    );
  }

  isPermissionSelected(permId: number): boolean {
    return this.selectedPermissions.has(permId);
  }

  togglePermission(permId: number) {
    if (this.selectedPermissions.has(permId)) {
      this.selectedPermissions.delete(permId);
    } else {
      this.selectedPermissions.add(permId);
    }
  }

  isAllSelected(): boolean {
    return this.filteredPermissions.length > 0 && 
           this.filteredPermissions.every(perm => this.selectedPermissions.has(perm.id));
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.filteredPermissions.forEach(perm => this.selectedPermissions.delete(perm.id));
    } else {
      this.filteredPermissions.forEach(perm => this.selectedPermissions.add(perm.id));
    }
  }

  save() {
    if (this.roleForm.valid) {
      const formValue = this.roleForm.value;
      const result = {
        ...formValue,
        permissions: Array.from(this.selectedPermissions)
      };
      this.activeModal.close(result);
    }
  }
} 